import { BufferAttribute, BufferGeometry, Color, Group, Mesh, NormalBlending, ShaderMaterial } from 'three';
import type { Object3D } from 'three';
import { ATMOSPHERE, NOISE, WORLD_UNIFORMS } from '@cloudpearl/assets';
import type { SharedUniforms } from '@cloudpearl/assets';

const VS = /* glsl */ `
attribute vec3 aTangent;
attribute float aSide;
attribute float aU;
uniform float uAge;
varying float vU;
varying float vSide;
varying vec3 vWorldPos;
void main() {
  // A ribbon that always faces the camera, at least ~1.5 px wide however far away.
  vec4 wc = modelMatrix * vec4(position, 1.0);
  vec3 toCam = normalize(cameraPosition - wc.xyz);
  vec3 across = normalize(cross(aTangent, toCam) + vec3(1e-5, 0.0, 0.0));
  float dist = distance(cameraPosition, wc.xyz);
  float w = max(0.07, dist * 0.0016);
  wc.xyz += across * aSide * w;
  vU = aU;
  vSide = aSide;
  vWorldPos = wc.xyz;
  gl_Position = projectionMatrix * viewMatrix * wc;
}
`;

const FS = /* glsl */ `
${WORLD_UNIFORMS}
${NOISE}
${ATMOSPHERE}
uniform vec3 uColor;
uniform float uAge;
varying float vU;
varying float vSide;
varying vec3 vWorldPos;
void main() {
  // Soft across the ribbon, a gentle shimmer travelling along it, older runs fainter.
  float core = 1.0 - smoothstep(0.2, 1.0, abs(vSide));
  float shimmer = 0.75 + 0.25 * sin(vU * 0.35 - uTime * 1.6);
  float a = core * shimmer * mix(0.55, 0.16, uAge);
  vec3 c = applyFog(uColor * 1.1, vWorldPos);
  gl_FragColor = vec4(c, a);
}
`;

const MAX_RUNS = 40;
const STEP = 2;

/**
 * Ghost trails (solo): every earlier run on this world drawn as a faint ribbon through the air
 * along the path the pearl took — over a session they build up into a map of every line you've
 * tried. Cleared when the world changes.
 */
export class GhostTrails {
  readonly group = new Group();
  private readonly runs: Mesh[] = [];
  private pts: number[] = [];
  private lx = Number.NaN;
  private lz = 0;
  private readonly color = new Color('#fdfaf6');

  constructor(
    parent: Object3D,
    private readonly shared: SharedUniforms,
  ) {
    parent.add(this.group);
  }

  setColor(hex: string): void {
    this.color.set(hex);
    for (const m of this.runs) ((m.material as ShaderMaterial).uniforms['uColor']!.value as Color).copy(this.color);
  }

  /** Called every frame of a run with the pearl's centre. */
  record(x: number, y: number, z: number): void {
    if (!Number.isNaN(this.lx) && (x - this.lx) * (x - this.lx) + (z - this.lz) * (z - this.lz) < STEP * STEP) return;
    this.pts.push(x, y, z);
    this.lx = x;
    this.lz = z;
  }

  /** The run ended: keep its path. */
  commit(): void {
    const p = this.pts;
    this.pts = [];
    this.lx = Number.NaN;
    const n = p.length / 3;
    if (n < 3) return;
    const pos = new Float32Array(n * 2 * 3);
    const tan = new Float32Array(n * 2 * 3);
    const side = new Float32Array(n * 2);
    const u = new Float32Array(n * 2);
    let len = 0;
    for (let i = 0; i < n; i++) {
      const a = Math.max(0, i - 1) * 3;
      const b = Math.min(n - 1, i + 1) * 3;
      let tx = p[b]! - p[a]!;
      let ty = p[b + 1]! - p[a + 1]!;
      let tz = p[b + 2]! - p[a + 2]!;
      const l = Math.hypot(tx, ty, tz) || 1;
      tx /= l;
      ty /= l;
      tz /= l;
      if (i > 0) len += Math.hypot(p[i * 3]! - p[i * 3 - 3]!, p[i * 3 + 1]! - p[i * 3 - 2]!, p[i * 3 + 2]! - p[i * 3 - 1]!);
      for (let s = 0; s < 2; s++) {
        const k = i * 2 + s;
        pos.set([p[i * 3]!, p[i * 3 + 1]!, p[i * 3 + 2]!], k * 3);
        tan.set([tx, ty, tz], k * 3);
        side[k] = s ? 1 : -1;
        u[k] = len;
      }
    }
    const idx = new Uint32Array((n - 1) * 6);
    for (let i = 0; i < n - 1; i++) idx.set([i * 2, i * 2 + 1, i * 2 + 2, i * 2 + 1, i * 2 + 3, i * 2 + 2], i * 6);
    const g = new BufferGeometry();
    g.setAttribute('position', new BufferAttribute(pos, 3));
    g.setAttribute('aTangent', new BufferAttribute(tan, 3));
    g.setAttribute('aSide', new BufferAttribute(side, 1));
    g.setAttribute('aU', new BufferAttribute(u, 1));
    g.setIndex(new BufferAttribute(idx, 1));
    g.computeBoundingSphere();
    const mat = new ShaderMaterial({
      name: 'ghost-trail',
      uniforms: { ...this.shared.all(), uColor: { value: this.color.clone() }, uAge: { value: 0 } },
      vertexShader: VS,
      fragmentShader: FS,
      transparent: true,
      depthWrite: false,
      blending: NormalBlending,
    });
    const mesh = new Mesh(g, mat);
    mesh.frustumCulled = false;
    mesh.renderOrder = 6;
    this.group.add(mesh);
    this.runs.push(mesh);
    while (this.runs.length > MAX_RUNS) this.dispose1(this.runs.shift()!);
    // The newest run is brightest; older ones recede.
    this.runs.forEach((m, i) => ((m.material as ShaderMaterial).uniforms['uAge']!.value = this.runs.length > 1 ? 1 - i / (this.runs.length - 1) : 0));
  }

  /** Drop the run in progress without keeping it. */
  discard(): void {
    this.pts = [];
    this.lx = Number.NaN;
  }

  /** New world: forget every trail. */
  clear(): void {
    this.discard();
    for (const m of this.runs) this.dispose1(m);
    this.runs.length = 0;
  }

  get count(): number {
    return this.runs.length;
  }

  private dispose1(m: Mesh): void {
    m.removeFromParent();
    m.geometry.dispose();
    (m.material as ShaderMaterial).dispose();
  }

  dispose(): void {
    this.clear();
    this.group.removeFromParent();
  }
}
